<template>
  <riseup-header :flow-config-id="FlowConfigIdType.Prograd" />
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
      variant="discount"
    />
  </div>
  <div
    v-else
    class="signup"
  >
    <Descope
      :client="{ anonymousUserId: anonymousSession.getId(), flowConfigId, promptId, utmParams, contextPageReferrer }"
      :error-transformer="errorTransformer"
      :flow-id="descopeFlow"
      @error="handleDescopeError"
      @success="handleSuccess"
    />
  </div>
</template>

<script lang="ts" setup>
import { Descope, useSession } from '@descope/vue-sdk';
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import _ from 'lodash';
import { computed, onBeforeMount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DDLogs from '../../DDLogs';
import Segment from '../../Segment';
import { completeDescopeOtsSignup } from '../../api/loginApi';
import anonymousSession from '../../anonymousSession';
import useContentLoader from '../../composables/useContentLoader';
import { useOtsJob } from '../../services/otsConfigService';
import { FlowConfigIdType } from '../../types/otsConfig';
import { errorTransformer, handleDescopeError } from '../../utils/errorHandling';
import { clientWithoutInterceptors } from '../../utils/http';
import RiseupHeader from '../Common/RiseupHeader.vue';

const { setOtsJobConfig } = useOtsJob();
const router = useRouter();
const {
  toggleLoader, showContentLoader,
} = useContentLoader();
toggleLoader(true);
watch(useSession().isLoading, async newVal => {
  toggleLoader(newVal);
});

const { sessionToken } = useSession();
const utmParams = _.pickBy(router.currentRoute.value.query, (value, key) => (key.startsWith('utm_') && value));
const flowConfigId = FlowConfigIdType.Prograd;
const promptId = router.currentRoute.value.query.pid || router.currentRoute.value.query.utm_pvr;
const contextPageReferrer = document.referrer;

const route = useRoute();

onBeforeMount(async () => {
  const queryParam = route.query || null;
  if (!queryParam['descope-login-flow']) {
    await Segment.reset();
    Segment.identifyAnonymousUser();
  }
});

const handleSuccess = async (e: CustomEvent) => {
  toggleLoader(true);
  DDLogs.info('DescopeProgradOtsSignup handleSuccess');
  const token = sessionToken.value ?? e.detail.sessionJwt;

  if (!token) {
    throw new Error('Descope Signup flow failed to signup, user sessionJwt is empty');
  }
  const { product, etoroId } = router.currentRoute.value.query;
  const gclid = Cookies.get('gclid');

  const resp = await completeDescopeOtsSignup(
    token,
    utmParams,
    { flowConfigId, promptId },
    gclid,
    etoroId,
  );

  Segment.identifyCustomer(resp.customerId, resp.memberId);

  if (!e.detail.user.customAttributes.customerId) {
    Segment.trackUserGot('CustomerCreated_Marketing', {
      market: 'UK',
      product,
      gclid,
      customerId: resp.customerId,
      utmParams,
      otsParams: {
        flowConfigId,
        promptId,
      },
      etoroId,
    });
  }
  const { jobId } = resp;
  setOtsJobConfig(flowConfigId, jobId);

  if (router.currentRoute.value.query.utm_variant || router.currentRoute.value.query.utm_pvr) {
    DDLogs.warn(
      'OTS used with old utm_params',
      {
        customer_id: resp.customerId,
        utm_pvr: promptId,
        utm_var: flowConfigId,
      },
    );
  }

  router.push('/ots/prograd/connect');
};

const descopeFlow = computed(() => 'prograd-signup');

const onMetaBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return (userAgent.indexOf('FBAN') > -1) || (userAgent.indexOf('FBAV') > -1) || (userAgent.indexOf('Instagram') > -1);
};

const currentHash = window.location.hash;

if (onMetaBrowser() && !currentHash) {
  window.location.hash = '#FBK';
}

</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.signup {
  background-color: #F7F7F7;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

</style>
